import React from "react";
import {useTranslation} from 'react-i18next';
import {Link} from "react-router-dom";

interface FooterLink {
  label: string;
  path: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface SocialLink {
  label: string;
  url: string;
  icon: string;
}


const Footer: React.FC = () => {
  const {t} = useTranslation();

  const EXPLORE_LINKS: FooterLink[] = [
    {label: t('footer.links.browseGallery'), path: "/gallery"},
    {label: t('footer.links.featuredCollections'), path: "/browse-collections"},
    {label: t('footer.links.browsePlaces'), path: "/locations"}
  ];

  const SOCIAL_LINKS: SocialLink[] = [
    {
      label: t('footer.social.facebook'),
      url: "https://www.facebook.com/share/1LQpHfZb7V",
      icon: "fa-brands fa-facebook-f"
    },
    {label: t('footer.social.website'), url: "https://cvp.ihs.uj.edu.pl/", icon: "fa fa-globe"}
  ];

  const ABOUT_LINKS: FooterLink[] = [
    {label: t('footer.links.aboutUs'), path: "/page/about"},
    {label: t('footer.links.partners'), path: "/partners"},
    {label: t('footer.links.contactUs'), path: "/contact"}
  ];

  const LEGAL_LINKS: FooterLink[] = [
    {label: t('footer.legal.termsOfService'), path: "/page/terms-of-service"},
    {label: t('footer.legal.privacyPolicy'), path: "/page/privacy-policy"}
  ];

  return (
    <footer id="footer" className="bg-black text-gray-400">
      <div className="container mx-auto px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-16">
          <div>
            <Link to="/home" className="flex items-center mb-4">
              <div className="w-36 h-16 mr-2 relative">
                <img src="/images/logo-white.svg" alt="CVP Logo" className="h-full w-auto"/>
              </div>
            </Link>
            <p className="text-sm">{t('footer.description')}</p>
            <div className="mt-4 flex space-x-4">
              {SOCIAL_LINKS.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white cursor-pointer transition-colors"
                  aria-label={social.label}
                >
                  <i className={`${social.icon}`}></i>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-white font-medium mb-4">{t('footer.sections.explore')}</h3>
            <ul className="space-y-2 text-sm">
              {EXPLORE_LINKS.map((link, index) => (
                <li key={index}>
                  <Link to={link.path} className="hover:text-white cursor-pointer">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-medium mb-4">{t('footer.sections.about')}</h3>
            <ul className="space-y-2 text-sm">
              {ABOUT_LINKS.map((link, index) => (
                <li key={index}>
                  <Link to={link.path} className="hover:text-white cursor-pointer">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-10 pt-6 flex flex-col md:flex-row justify-between items-center">
          <p className="text-xs">{t('footer.copyright')}</p>
          <div className="mt-4 md:mt-0 flex space-x-4 text-xs">
            {LEGAL_LINKS.map((link, index) => (
              <Link key={index} to={link.path} className="hover:text-white cursor-pointer">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
